import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { AVATAR_URL_PREFIX } from "@/config/constants";
import { USER_ROLES } from "@/config/admin";
import { DEFAULT_PLAN_ID } from "@/config/plans";
import type { NavbarState, UserDisplayInfo } from "./types";

export function useNavbar(): NavbarState {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { credits, maxCredits } = useCredits();
  const { user, profile, signOut, loading } = useAuth();
  const { t, isRTL } = useLanguage();

  const handleSignOut = async () => {
    await signOut();
    toast.success(t.nav.logoutSuccess);
    navigate("/");
  };
  


  const isActive = (path: string) => currentPath === path;

  const creditsPercentage =
    maxCredits > 0 ? Math.round((credits / maxCredits) * 100) : 0;

  const displayName =
    profile?.display_name ||
    user?.email?.split("@")[0] ||
    t.nav.defaultUser;
  const planName = profile?.subscription_name || DEFAULT_PLAN_ID;
  const avatarUrl =
    profile?.avatar_url ||
    `${AVATAR_URL_PREFIX}?seed=${displayName}`;

  const userDisplayInfo: UserDisplayInfo = {
    displayName,
    planName,
    avatarUrl,
  };

  const isAdmin = profile?.role === USER_ROLES.ADMIN;

  return {
    isOpen,
    setIsOpen,
    currentPath,
    isActive,
    handleSignOut,
    user,
    authLoading: loading,
    profile,
    credits,
    maxCredits,
    creditsPercentage,
    userDisplayInfo,
    isAdmin,
    t,
    isRTL,
  };
}
